import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Button, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { listMyInspectionAppointmentsAll } from '../../inspection-appointments/services/inspection-appointmentService';
import { listMyLeavesAll } from '../../leaves/services/leaveService';
import { listPublicHolidaysAll } from '../../public-holidays/services/public-holidayService';
import { Leave } from '../../leaves/types/Leave';
import { PublicHoliday } from '../../public-holidays/types/PublicHoliday';
import { getLeaveTypeLabel } from '../../../utils';

const { Title, Text } = Typography;

interface PlanEntry {
  key: string;
  label: string;
  timeRange?: string;
  location?: string;
  proposalId?: number;
  proposalNumber?: string;
  status?: string;
  startDateTime?: string;
  endDateTime?: string;
  type: 'APPOINTMENT' | 'LEAVE' | 'PUBLIC_HOLIDAY';
  halfDay?: boolean;
  contractId?: string | null;
  contractUrl?: string | null;
  hasMissingContract?: boolean;
}

interface PlanRow {
  key: string;
  date: Dayjs;
  entries: Array<PlanEntry>;
}

const statusColorMap: Record<string, string> = {
  PLANNED: 'blue',
  APPROVED: 'green',
  COMPLETED: 'green',
  CANCELLED: 'red',
  LEAVE: 'warning',
  PUBLIC_HOLIDAY: 'magenta',
};

const resolveProposalId = (appointment: any): number | undefined => {
  const proposal = appointment?.proposal;
  const candidates = [
    appointment?.proposalId,
    appointment?.proposal_id,
    appointment?.proposal?.id,
    appointment?.proposal?.proposalId,
    appointment?.proposal?.proposal_id,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const parsed = Number(candidate);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
};

const resolveCompanyName = (appointment: any): string | undefined => {
  if (typeof appointment?.companyName === 'string') {
    return appointment.companyName;
  }
  if (typeof appointment?.company?.name === 'string') {
    return appointment.company.name;
  }
  if (typeof appointment?.proposal?.company?.name === 'string') {
    return appointment.proposal.company.name;
  }
  return undefined;
};

const resolveProposalNumber = (appointment: any): string | undefined => {
  const candidates = [
    appointment?.proposal?.proposalNumber,
    appointment?.proposalNumber,
    appointment?.proposal?.number,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const str = String(candidate).trim();
    if (str.length > 0) {
      return str;
    }
  }
  return undefined;
};

const resolveLocation = (appointment: any): string | undefined => {
  const addresses = [
    appointment?.proposal?.address,
    appointment?.company?.address,
    appointment?.address,
  ];

  const pickValue = (source: any, keys: string[]) => {
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    for (const key of keys) {
      const value = (source as any)[key];
      if (typeof value === 'string' && value.trim().length) {
        return value.trim();
      }
    }
    return undefined;
  };

  for (const address of addresses) {
    if (!address) continue;
    const district = pickValue(address, [
      'district',
      'districtName',
      'district_text',
      'county',
      'countyName',
      'ilce',
      'town',
    ]);
    const city = pickValue(address, ['city', 'cityName', 'province', 'provinceName', 'state', 'il']);
    const parts = [district, city].filter(Boolean);
    if (parts.length) {
      return parts.join(' / ');
    }
  }

  return undefined;
};

const InspectorWeeklyPlanCard: React.FC = () => {
  const [rangeStart, setRangeStart] = useState<Dayjs>(() => dayjs().startOf('day'));
  const [appointments, setAppointments] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const rangeEnd = useMemo(() => rangeStart.add(6, 'day').endOf('day'), [rangeStart]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, idx) => rangeStart.add(idx, 'day')),
    [rangeStart],
  );

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        setLoading(true);
        const appointmentParams = new URLSearchParams({
          'startDate.greaterThanOrEqual': rangeStart.toISOString(),
          'startDate.lessThanOrEqual': rangeEnd.toISOString(),
          sort: 'startDate,ASC',
        });
        const leaveParams = new URLSearchParams({
          'startDate.lessThanOrEqual': rangeEnd.toISOString(),
          'endDate.greaterThanOrEqual': rangeStart.toISOString(),
          sort: 'startDate,ASC',
        });
        const [appointmentRes, leaveRes, publicHolidayRes] = await Promise.all([
          listMyInspectionAppointmentsAll(appointmentParams.toString()),
          listMyLeavesAll(leaveParams.toString()),
          listPublicHolidaysAll(),
        ]);
        if (!ignore) {
          setAppointments(Array.isArray(appointmentRes) ? appointmentRes : []);
          setLeaves(Array.isArray(leaveRes) ? leaveRes : []);
          setPublicHolidays(Array.isArray(publicHolidayRes) ? publicHolidayRes : []);
        }
      } catch (error) {
        if (!ignore) {
          setAppointments([]);
          setLeaves([]);
          setPublicHolidays([]);
        }
        console.error('InspectorWeeklyPlanCard data fetch failed:', error);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [rangeStart, rangeEnd]);

  const rows: PlanRow[] = useMemo(() => {
    return days.map((day) => {
      const dayStart = day.startOf('day');
      const dayEnd = day.endOf('day');

      const appointmentEntries: PlanEntry[] = appointments
        .filter((appointment) => {
          const startRaw = appointment?.startDate ?? appointment?.start_date ?? appointment?.planningAt;
          if (!startRaw) return false;
          const startMoment = dayjs(startRaw);
          if (!startMoment.isValid()) return false;
          return !startMoment.isBefore(dayStart) && !startMoment.isAfter(dayEnd);
        })
        .map((appointment): PlanEntry => {
          const startMoment = dayjs(appointment?.startDate ?? appointment?.planningAt ?? appointment?.start_date);
          const endMomentCandidate = appointment?.endDate ?? appointment?.end_date ?? appointment?.planningEndDate;
          const endMoment = endMomentCandidate ? dayjs(endMomentCandidate) : null;
          const endValid = endMoment && endMoment.isValid() ? endMoment : startMoment;
          const status = (appointment?.status ?? 'PLANNED').toString().toUpperCase();
          const proposalId = resolveProposalId(appointment);
          const proposalNumber = resolveProposalNumber(appointment);
          const companyName = resolveCompanyName(appointment);
          const label = companyName ?? proposalNumber ?? 'Plan';
          const timeRange = `${startMoment.format('HH:mm')} - ${endValid.format('HH:mm')}`;
          const location = resolveLocation(appointment);
          const contractId = [
            appointment?.contractId,
            appointment?.contract_id,
          ].find((val) => typeof val === 'string' && val.trim().length > 0) ?? null;
          const contractUrl = [
            appointment?.contractUrl,
            appointment?.contract_url,
          ].find((val) => typeof val === 'string' && val.trim().length > 0) ?? null;
          const hasMissingContract = !contractId || !contractUrl;
          return {
            key: `${appointment?.id ?? startMoment.valueOf()}`,
            label,
            timeRange,
            location,
            proposalId,
            proposalNumber,
            status,
            startDateTime: startMoment.format('DD MMMM YYYY HH:mm'),
            endDateTime: endValid.format('DD MMMM YYYY HH:mm'),
            type: 'APPOINTMENT' as const,
            contractId,
            contractUrl,
            hasMissingContract,
          };
        });

      const leaveEntries: PlanEntry[] = (leaves || [])
        .filter((leave) => {
          const startRaw = leave?.startDate;
          const endRaw = leave?.endDate;
          if (!startRaw || !endRaw) return false;
          const startMoment = dayjs(startRaw);
          const endMoment = dayjs(endRaw);
          if (!startMoment.isValid() || !endMoment.isValid()) return false;
          return startMoment.isBefore(dayEnd) && endMoment.isAfter(dayStart);
        })
        .map((leave): PlanEntry => {
          const startMoment = dayjs(leave?.startDate);
          const endMoment = dayjs(leave?.endDate);
          const coverStart = startMoment.isAfter(dayStart) ? startMoment : dayStart;
          const coverEnd = endMoment.isBefore(dayEnd) ? endMoment : dayEnd;
          const isFullDay = coverStart.isSame(dayStart) && coverEnd.isSame(dayEnd);
          const durationLabel = isFullDay
            ? 'Tam Gün'
            : `${coverStart.format('HH:mm')} - ${coverEnd.format('HH:mm')}`;
          const typeKey = typeof leave?.type === 'string' ? leave.type : 'LEAVE';
          const typeLabel = getLeaveTypeLabel(typeKey);
          return {
            key: `leave-${leave?.id ?? coverStart.valueOf()}`,
            label: `İzin • ${typeLabel}`,
            timeRange: durationLabel,
            location: undefined,
            proposalId: undefined,
            proposalNumber: undefined,
            status: typeLabel,
            startDateTime: coverStart.format('HH:mm'),
            endDateTime: coverEnd.format('HH:mm'),
            type: 'LEAVE' as const,
          };
        });

      const holidayEntries = publicHolidays
        .map((holiday): PlanEntry | null => {
          const startMoment = holiday?.startDate ? dayjs(holiday.startDate) : null;
          if (!startMoment || !startMoment.isValid()) {
            return null;
          }
          const rawEndMoment = holiday?.endDate ? dayjs(holiday.endDate) : startMoment;
          const endMoment = rawEndMoment.isValid() ? rawEndMoment : startMoment;
          if (endMoment.isBefore(dayStart) || startMoment.isAfter(dayEnd)) {
            return null;
          }
          const coverStart = startMoment.isAfter(dayStart) ? startMoment : dayStart;
          const coverEnd = endMoment.isBefore(dayEnd) ? endMoment : dayEnd;
          const isHalfDay = Boolean(holiday?.halfDay);
          const isFullDay = !Boolean(holiday?.halfDay);
          const isTrailingZeroCover =
            isHalfDay &&
            coverStart.isSame(dayStart) &&
            coverEnd.isSame(dayStart) &&
            startMoment.isBefore(dayStart, 'day');
          const isZeroDurationHalfDay = isHalfDay && coverStart.isSame(coverEnd);

          console.log('Holiday Entry:', isFullDay, dayStart, dayEnd)

          let timeLabel: string;
          if (isTrailingZeroCover) {
            timeLabel = 'Tam Gün';
          } else if (isZeroDurationHalfDay) {
            timeLabel = 'Yarım Gün';
          } else if (isHalfDay) {
            timeLabel = 'Yarım Gün';
          } else if (isFullDay) {
            timeLabel = 'Tam Gün';
          } else {
            timeLabel = `${coverStart.format('HH:mm')} - ${coverEnd.format('HH:mm')}`;
          }
          const holidayLabel = holiday?.name?.toString().trim() || 'Resmi Tatil';
          const entry: PlanEntry = {
            key: `holiday-${holiday?.id ?? startMoment.valueOf()}-${day.format('YYYYMMDD')}`,
            label: `Resmi Tatil • ${holidayLabel}`,
            timeRange: timeLabel,
            location: undefined,
            proposalId: undefined,
            proposalNumber: undefined,
            status: holidayLabel,
            startDateTime: isTrailingZeroCover || isZeroDurationHalfDay ? undefined : coverStart.format('HH:mm'),
            endDateTime: isTrailingZeroCover || isZeroDurationHalfDay ? undefined : coverEnd.format('HH:mm'),
            type: 'PUBLIC_HOLIDAY' as const,
            halfDay: isHalfDay,
          };
          return entry;
        })
        .filter((entry): entry is PlanEntry => entry !== null);

      const entries: PlanEntry[] = [...appointmentEntries, ...leaveEntries, ...holidayEntries];
      entries.sort((a, b) => a.label.localeCompare(b.label, 'tr'));

      return {
        key: day.format('YYYY-MM-DD'),
        date: day,
        entries,
      };
    });
  }, [appointments, leaves, publicHolidays, days]);

  const todayStart = useMemo(() => dayjs().startOf('day'), []);
  const maxForwardEnd = useMemo(() => dayjs().startOf('day').add(6, 'day').endOf('day'), []);
  const canGoNext = useMemo(() => rangeStart.isBefore(todayStart), [rangeStart, todayStart]);

  const columns: ColumnsType<PlanRow> = useMemo(
    () => [
      {
        title: 'Tarih',
        dataIndex: 'date',
        key: 'date',
        width: 180,
        render: (value: Dayjs) => (
          <div>
            <strong>{value.format('DD MMMM YYYY')}</strong>
            <div style={{ color: '#8c8c8c' }}>{value.format('dddd')}</div>
          </div>
        ),
      },
      {
        title: 'Planlar',
        dataIndex: 'entries',
        key: 'entries',
        render: (entries: PlanRow['entries'], row: PlanRow) => {
          const isSunday = row?.date ? row.date.day() === 0 : false;
          const isPastOrToday = row?.date
            ? row.date.isSame(todayStart, 'day') || row.date.isBefore(todayStart, 'day')
            : false;
          if (!entries.length) {
            if (isSunday) {
              const sundayMessage = isPastOrToday ? 'Hafta Tatili' : 'Plan henüz belirlenmedi.';
              const sundayColor = isPastOrToday ? 'purple' : 'geekblue';
              return <Tag color={sundayColor}>{sundayMessage}</Tag>;
            }

            const emptyMessage = isPastOrToday ? 'Saha Dışı Çalışma Günü.' : 'Plan henüz belirlenmedi.';
            const tagColor = isPastOrToday ? 'cyan' : 'geekblue';

            return <Tag color={tagColor}>{emptyMessage}</Tag>;
          }

          return (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              {entries.map((entry) => {
                let color =
                  entry.type === 'LEAVE'
                    ? statusColorMap.LEAVE
                    : entry.type === 'PUBLIC_HOLIDAY'
                      ? statusColorMap.PUBLIC_HOLIDAY
                      : statusColorMap[entry.status ?? 'PLANNED'] ?? 'blue';
                if (entry.type === 'APPOINTMENT' && entry.hasMissingContract) {
                  color = 'orange';
                }
                const tooltipContent = entry.type === 'APPOINTMENT'
                  ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {entry.proposalNumber ? (
                        <div>
                          <strong>Teklif No:</strong> {entry.proposalNumber}
                        </div>
                      ) : null}
                      {entry.startDateTime? (
                        <div>
                          <strong>Başlangıç:</strong> {entry.startDateTime}
                        </div>
                      ) : null}
                      {entry.endDateTime? (
                        <div>
                          <strong>Bitiş:</strong> {entry.endDateTime}
                        </div>
                      ) : null}
                      {entry.location ? (
                        <div>
                          <strong>Konum:</strong> {entry.location}
                        </div>
                      ) : null}
                      {entry.status ? (
                        <div>
                          <strong>Durum:</strong> {entry.status}
                        </div>
                      ) : null}
                      {entry.hasMissingContract ? (
                        <div>
                          <strong>Sözleşme:</strong> ISG Katip ID eksik veya sözleşme yüklü değil
                        </div>
                      ) : null}
                    </div>
                  )
                  : entry.type === 'LEAVE'
                    ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {entry.timeRange ? (
                          <div>
                            <strong>Süre:</strong> {entry.timeRange}
                          </div>
                        ) : null}
                        {entry.startDateTime ? (
                          <div>
                            <strong>Başlangıç:</strong> {entry.startDateTime}
                          </div>
                        ) : null}
                        {entry.endDateTime ? (
                          <div>
                            <strong>Bitiş:</strong> {entry.endDateTime}
                          </div>
                        ) : null}
                        {entry.status ? (
                          <div>
                            <strong>Tür:</strong> {entry.status}
                          </div>
                        ) : null}
                      </div>
                    )
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {entry.label ? (
                          <div>
                            <strong>Resmi Tatil:</strong> {entry.label.replace('Resmi Tatil • ', '')}
                          </div>
                        ) : null}
                        {entry.timeRange ? (
                          <div>
                            {entry.timeRange}
                          </div>
                        ) : null}
                        {entry.startDateTime && entry.halfDay ? (
                          <div>
                            <strong>Başlangıç:</strong> {entry.startDateTime}
                          </div>
                        ) : null}
                        {entry.endDateTime && entry.halfDay ? (
                          <div>
                            <strong>Bitiş:</strong> {entry.endDateTime}
                          </div>
                        ) : null}
                        {entry.halfDay ? (
                          <div>
                            <strong>Detay:</strong> Yarım Gün
                          </div>
                        ) : null}
                      </div>
                    );
                const handleClick = () => {
                  if (!entry.proposalId || entry.type !== 'APPOINTMENT') {
                    return;
                  }
                  navigate(`/inspector/planning/detail/${entry.proposalId}`);
                };
                const isClickable = entry.type === 'APPOINTMENT' && entry.proposalId != null;
                return (
                  <Tooltip key={entry.key} title={tooltipContent} placement='topLeft'>
                    <Tag
                      color={color}
                      onClick={isClickable ? handleClick : undefined}
                      style={{
                        cursor: isClickable ? 'pointer' : 'default',
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '6px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        maxWidth: '100%',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{entry.label}</span>
                      {entry.timeRange ? <span>{entry.timeRange}</span> : null}
                      {entry.location ? <span>{entry.location}</span> : null}
                    </Tag>
                  </Tooltip>
                );
              })}
            </Space>
          );
        },
      },
    ],
    [navigate, todayStart],
  );

  const dataSource = rows;

  const handlePrev = () => {
    setRangeStart((prev) => prev.add(-7, 'day'));
  };
  const handleNext = () => {
    setRangeStart((prev) => {
      if (!prev.isBefore(todayStart)) {
        return prev;
      }
      const nextStart = prev.add(7, 'day');
      const nextEnd = nextStart.add(6, 'day').endOf('day');
      if (nextStart.isAfter(todayStart) || nextEnd.isAfter(maxForwardEnd)) {
        return prev;
      }
      return nextStart;
    });
  };
  const handleToday = () => {
    setRangeStart(dayjs().startOf('day'));
  };

  return (
    <Card
      className='br-30px mt-16px'
      bodyStyle={{ padding: 20 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0, fontSize: 18 }}>
            Görevlerim
          </Title>
          <Space>
            <Button size='small' onClick={handlePrev}>
              Önceki 7 Gün
            </Button>
            <Button size='small' onClick={handleToday}>
              Bugün
            </Button>
            <Button size='small' onClick={handleNext} disabled={!canGoNext}>
              Sonraki 7 Gün
            </Button>
          </Space>
        </div>
      }
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        rowKey='key'
        pagination={false}
        loading={loading}
        locale={{ emptyText: 'Bu tarih aralığında plan bulunmamaktadır.' }}
      />
    </Card>
  );
};

export default InspectorWeeklyPlanCard;
